import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VERIFIED_MEMBERS } from './VerifyCertificate';
import { 
    Award, 
    ShieldCheck, 
    CheckCircle2, 
    Building2, 
    Sparkles, 
    ArrowRight, 
    ExternalLink, 
    FileCheck2, 
    Mail, 
    GraduationCap, 
    Briefcase,
    ChevronDown,
    ChevronUp,
    MessageCircle
} from 'lucide-react';

const Pricing = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            q: "What does the nominal ₹100 Fellow induction fee cover?",
            a: "The nominal ₹100 fee is a one-time verification and induction charge covering the full 10-Year Strategic Academic Council Tenure (2026–2036). It includes cryptographic certificate generation with QR verification, permanent registration in the National Verified Member Registry (ANRD/AM/2026/XXX), Council Directory listing, and appointment credentials. There are zero recurring annual fees during the 10-year term."
        },
        {
            q: "Can the 10-year council appointment be renewed after 2036?",
            a: "Yes. Towards the completion of the 2026–2036 strategic cycle, active council fellows with distinguished contributions, peer reviews, and academic participation will be eligible for emeritus re-appointment or senior honorary fellow elevation by the Executive Directorate."
        },
        {
            q: "How can an engineering college or university sign an Institutional MoU?",
            a: "Colleges can initiate an institutional partnership by requesting our standard Bilateral Academic MoU framework. Our Directorate coordinates with college management to formalize joint FDPs, student internships, conference technical co-sponsorships, and NAAC/NBA criteria documentation."
        },
        {
            q: "How are Arivu Nest credentials verified by universities and accreditation bodies?",
            a: "Every fellow is issued a permanent Reference ID (e.g., ANRD/AM/2026/049) and a QR-coded digital certificate. Any institution, employer, or accreditation committee can verify authenticity 24/7 in real time at arivunest.com/verify."
        },
        {
            q: "How does the Academic Ambassador & Referral Program work?",
            a: "Active Academic Council Fellows can nominate peers and research colleagues for induction. For each verified fellow inducted through your recommendation, you earn 10 Academic Merit Points, elevating your national council standing and unlocking senior mentorship ranks."
        },
        {
            q: "Can Arivu Nest R&D co-sponsor our department's national conference or symposium?",
            a: "Yes. Under our Institutional MoU framework, Arivu Nest R&D serves as a technical co-sponsor, providing peer-review committee support, keynote addresses, certificate verification infrastructure, and publication mentorship."
        }
    ];

    return (
        <div style={styles.page}>
            {/* Top Institutional Header */}
            <div style={styles.heroSection}>
                <div style={styles.container}>
                    <div style={styles.badgePill}>
                        <ShieldCheck size={15} color="#059669" />
                        <span>Apex Multidisciplinary Research & Academic Council &bull; Govt. of India MSME Registered</span>
                    </div>
                    <h1 style={styles.heroTitle}>
                        Institutional Memberships &amp; <span style={{ color: '#059669' }}>R&amp;D Services</span>
                    </h1>
                    <p style={styles.heroSubtitle}>
                        Empowering academicians, universities, research scholars, and industry partners with verifiable academic credentials, collaborative research frameworks, and bilateral MoUs.
                    </p>

                    {/* Trust Highlights */}
                    <div style={styles.trustGrid}>
                        <div style={styles.trustItem}>
                            <span style={styles.trustValue}>{VERIFIED_MEMBERS.length}+</span>
                            <span style={styles.trustLabel}>Verified Academic Fellows</span>
                        </div>
                        <div style={styles.trustItem}>
                            <span style={styles.trustValue}>MSME</span>
                            <span style={styles.trustLabel}>UDYAM-TN-32-0048110</span>
                        </div>
                        <div style={styles.trustItem}>
                            <span style={styles.trustValue}>100%</span>
                            <span style={styles.trustLabel}>Verifiable Credentials</span>
                        </div>
                        <div style={styles.trustItem}>
                            <span style={styles.trustValue}>10 Years</span>
                            <span style={styles.trustLabel}>Council Tenure (2026–2036)</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Core Offerings Grid */}
            <div style={styles.container}>
                <div style={styles.offeringsGrid}>

                    {/* 1. Academic & Advisory Council Induction (Most Popular) */}
                    <div style={{ ...styles.card, ...styles.featuredCard }}>
                        <div style={styles.featuredBadge}>
                            <Sparkles size={13} style={{ marginRight: '4px' }} />
                            10-YEAR STRATEGIC TENURE (2026–2036)
                        </div>

                        <div style={styles.cardHeader}>
                            <div style={styles.iconCircleEmerald}>
                                <Award size={26} color="#059669" />
                            </div>
                            <h3 style={styles.cardTitle}>Academic Council Fellow</h3>
                            <p style={styles.targetAudience}>For Professors, Asst. Professors &amp; Research Scholars</p>
                        </div>

                        <div style={styles.priceContainer}>
                            <div style={styles.priceValue}>₹100</div>
                            <div style={styles.priceSubtitle}>One-Time Verification &amp; Induction Fee</div>
                            <div style={styles.tenureTag}>10-Year Council Tenure (2026–2036) &bull; Zero Annual Dues</div>
                        </div>

                        <ul style={styles.featuresList}>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#059669" style={styles.checkIcon} />
                                <span>Official Verifiable Credential (<strong>ANRD/AM/2026/XXX</strong>)</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#059669" style={styles.checkIcon} />
                                <span><strong>10-Year Official Tenure (2026–2036)</strong> on the Council</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#059669" style={styles.checkIcon} />
                                <span>Permanent listing on the <strong>National Council Directory</strong></span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#059669" style={styles.checkIcon} />
                                <span>High-Resolution Digital Certificate with instant QR lookup</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#059669" style={styles.checkIcon} />
                                <span>Eligibility for Editorial Panels &amp; Conference Peer Reviews</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#059669" style={styles.checkIcon} />
                                <span>Earn 10 points per colleague referral on the National Board</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#059669" style={styles.checkIcon} />
                                <span>Official Letter of Appointment for Academic Portfolios &amp; NAAC/NBA files</span>
                            </li>
                        </ul>

                        <a 
                            href="https://forms.gle/UfkUncKELYenUJE46" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={styles.primaryBtn}
                        >
                            <span>Apply for Induction (₹100)</span>
                            <ExternalLink size={16} />
                        </a>
                        <p style={styles.btnDisclaimer}>Instant online submission &bull; Same-day verification &amp; credential dispatch</p>
                    </div>

                    {/* 2. Institutional MoU & College Partnerships */}
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <div style={styles.iconCircleNavy}>
                                <Building2 size={26} color="#1e40af" />
                            </div>
                            <h3 style={styles.cardTitle}>Institutional MoU</h3>
                            <p style={styles.targetAudience}>For Engineering Colleges, Universities &amp; Institutes</p>
                        </div>

                        <div style={styles.priceContainer}>
                            <div style={{ ...styles.priceValue, color: '#1e3a8a' }}>Bilateral MoU</div>
                            <div style={styles.priceSubtitle}>Formal Institutional Partnership</div>
                            <div style={{ ...styles.tenureTag, background: '#eff6ff', color: '#1d4ed8' }}>NAAC / NBA Alignment Ready</div>
                        </div>

                        <ul style={styles.featuresList}>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#2563eb" style={styles.checkIcon} />
                                <span>Formal Bilateral MoU signed with Directorate</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#2563eb" style={styles.checkIcon} />
                                <span>Joint Faculty Development Programs (FDPs) &amp; Seminars</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#2563eb" style={styles.checkIcon} />
                                <span>Technical Co-Sponsorship for National &amp; Intl Conferences</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#2563eb" style={styles.checkIcon} />
                                <span>Establishment of Student Innovation Chapters</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#2563eb" style={styles.checkIcon} />
                                <span>Collaborative Research Grant Proposals &amp; Project Incubation</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#2563eb" style={styles.checkIcon} />
                                <span>Executive Keynotes &amp; Mentorship by Dr. S. Sabitha Shunmugapriya, Ph.D</span>
                            </li>
                        </ul>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <a 
                                href="https://mail.google.com/mail/?view=cm&fs=1&to=dr.sabitha@arivunest.com&su=Institutional%20MoU%20Inquiry%20-%20Arivu%20Nest%20R%26D" target="_blank" rel="noopener noreferrer" 
                                style={styles.navyBtn}
                            >
                                <span>Email Executive Directorate</span>
                                <Mail size={16} />
                            </a>
                            <button 
                                onClick={() => navigate('/contact')} 
                                style={{ ...styles.outlineBtn, borderColor: '#1e40af', color: '#1e40af', padding: '10px' }}
                            >
                                <span>Submit MoU Inquiry Online</span>
                                <ArrowRight size={15} />
                            </button>
                        </div>
                        <p style={styles.btnDisclaimer}>Direct coordination with Executive Directorate</p>
                    </div>

                    {/* 3. Corporate & Industry R&D Consultancy */}
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <div style={styles.iconCircleSlate}>
                                <Briefcase size={26} color="#0f766e" />
                            </div>
                            <h3 style={styles.cardTitle}>Corporate &amp; MSME R&amp;D</h3>
                            <p style={styles.targetAudience}>For Tech Startups, MSMEs &amp; Industrial Units</p>
                        </div>

                        <div style={styles.priceContainer}>
                            <div style={{ ...styles.priceValue, color: '#0f766e' }}>Scope-Based</div>
                            <div style={styles.priceSubtitle}>Project or Annual Retainer</div>
                            <div style={{ ...styles.tenureTag, background: '#f0fdfa', color: '#0f766e' }}>Industry-Academia Collaboration</div>
                        </div>

                        <ul style={styles.featuresList}>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#0f766e" style={styles.checkIcon} />
                                <span>Applied AI / Machine Learning Integration Advisory</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#0f766e" style={styles.checkIcon} />
                                <span>Computational Linguistics &amp; Natural Language Solutions</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#0f766e" style={styles.checkIcon} />
                                <span>Prior-Art Patent Search &amp; IP Filing Guidance</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#0f766e" style={styles.checkIcon} />
                                <span>Technical Feasibility Audits &amp; Code Review Reports</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#0f766e" style={styles.checkIcon} />
                                <span>Sponsored Multidisciplinary R&amp;D Project Incubation</span>
                            </li>
                        </ul>

                        <button 
                            onClick={() => navigate('/contact')} 
                            style={styles.outlineBtn}
                        >
                            <span>Consult R&amp;D Directorate</span>
                            <ArrowRight size={16} />
                        </button>
                        <p style={styles.btnDisclaimer}>Confidential Non-Disclosure Agreement (NDA) compliant</p>
                    </div>

                    {/* 4. Student Innovation & Research Internships */}
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <div style={styles.iconCircleAmber}>
                                <GraduationCap size={26} color="#b45309" />
                            </div>
                            <h3 style={styles.cardTitle}>Student Research Internships</h3>
                            <p style={styles.targetAudience}>For UG / PG Engineers &amp; Research Scholars</p>
                        </div>

                        <div style={styles.priceContainer}>
                            <div style={{ ...styles.priceValue, color: '#b45309' }}>Cohort-Based</div>
                            <div style={styles.priceSubtitle}>Summer &amp; Semester Internships</div>
                            <div style={{ ...styles.tenureTag, background: '#fffbeb', color: '#b45309' }}>Practical Research Experience</div>
                        </div>

                        <ul style={styles.featuresList}>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#b45309" style={styles.checkIcon} />
                                <span>Guided Hands-on Research Project Execution</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#b45309" style={styles.checkIcon} />
                                <span>Mentorship for Paper Publication in Peer-Reviewed Journals</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#b45309" style={styles.checkIcon} />
                                <span>Verifiable Internship Certificate with Online Ref ID</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#b45309" style={styles.checkIcon} />
                                <span>Letter of Recommendation (LOR) from Executive Directorate</span>
                            </li>
                            <li style={styles.featureItem}>
                                <CheckCircle2 size={17} color="#b45309" style={styles.checkIcon} />
                                <span>Direct 1-on-1 Code and Research Methodology Reviews</span>
                            </li>
                        </ul>

                        <button 
                            onClick={() => navigate('/internships')} 
                            style={styles.amberBtn}
                        >
                            <span>Explore Internship Tracks</span>
                            <ArrowRight size={16} />
                        </button>
                        <p style={styles.btnDisclaimer}>Structured curriculum &bull; Industry-recognized credentials</p>
                    </div>

                </div>
            </div>

            {/* Comprehensive Comparison Matrix */}
            <div style={styles.matrixSection}>
                <div style={styles.container}>
                    <div style={styles.sectionHeader}>
                        <h2 style={styles.sectionTitle}>Institutional Deliverables Matrix</h2>
                        <p style={styles.sectionSubtitle}>Detailed comparison of academic entitlements and institutional benefits</p>
                    </div>

                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, textAlign: 'left', minWidth: '240px' }}>Entitlement / Deliverable</th>
                                    <th style={styles.th}>Academic Fellow (₹100)</th>
                                    <th style={styles.th}>Institutional MoU</th>
                                    <th style={styles.th}>Corporate R&amp;D</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={styles.tdTitle}>Verifiable Digital Credential &amp; Ref ID</td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                </tr>
                                <tr style={styles.altRow}>
                                    <td style={styles.tdTitle}>National Council Directory Listing</td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}>Departmental</td>
                                    <td style={styles.tdCenter}>Industry Partner</td>
                                </tr>
                                <tr>
                                    <td style={styles.tdTitle}>Bilateral MoU Agreement Document</td>
                                    <td style={styles.tdCenter}><span style={{ color: '#94a3b8' }}>&mdash;</span></td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                </tr>
                                <tr style={styles.altRow}>
                                    <td style={styles.tdTitle}>Conference Technical Co-Sponsorship</td>
                                    <td style={styles.tdCenter}>Reviewer / Session Chair</td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}>Keynote / Sponsorship</td>
                                </tr>
                                <tr>
                                    <td style={styles.tdTitle}>Faculty Development Programs (FDPs)</td>
                                    <td style={styles.tdCenter}>Individual Access</td>
                                    <td style={styles.tdCenter}>Institution-Wide</td>
                                    <td style={styles.tdCenter}>Custom Corporate</td>
                                </tr>
                                <tr style={styles.altRow}>
                                    <td style={styles.tdTitle}>NAAC / NBA Criteria Supporting Records</td>
                                    <td style={styles.tdCenter}>Appointment Letter</td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}><span style={{ color: '#94a3b8' }}>&mdash;</span></td>
                                </tr>
                                <tr>
                                    <td style={styles.tdTitle}>Direct Directorate Mentorship</td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                    <td style={styles.tdCenter}><CheckCircle2 size={18} color="#059669" /></td>
                                </tr>
                                <tr style={styles.altRow}>
                                    <td style={styles.tdTitle}>Appointed Tenure / Duration</td>
                                    <td style={styles.tdCenter}><strong>10-Year Tenure (2026–2036) &bull; Zero Annual Fees</strong></td>
                                    <td style={styles.tdCenter}>Bilateral MoU (1 to 3 Years)</td>
                                    <td style={styles.tdCenter}>Project / Retainer</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Frequently Asked Questions */}
            <div style={styles.container}>
                <div style={styles.faqSection}>
                    <div style={styles.sectionHeader}>
                        <h2 style={styles.sectionTitle}>Frequently Asked Questions</h2>
                        <p style={styles.sectionSubtitle}>Clear answers regarding council induction, 10-year tenure, institutional MoUs, and credential verification</p>
                    </div>

                    <div style={styles.faqList}>
                        {faqs.map((faq, idx) => (
                            <div 
                                key={idx} 
                                style={styles.faqItem}
                                onClick={() => toggleFaq(idx)}
                            >
                                <div style={styles.faqQuestion}>
                                    <span style={styles.faqQText}>{faq.q}</span>
                                    {openFaq === idx ? (
                                        <ChevronUp size={20} color="#059669" />
                                    ) : (
                                        <ChevronDown size={20} color="#64748b" />
                                    )}
                                </div>
                                {openFaq === idx && (
                                    <div style={styles.faqAnswer}>
                                        <p style={styles.faqAText}>{faq.a}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Executive Inquiry Callout */}
            <div style={styles.container}>
                <div style={styles.ctaBanner}>
                    <div style={styles.ctaContent}>
                        <h3 style={styles.ctaTitle}>Ready to Partner or Nominate Your Faculty?</h3>
                        <p style={styles.ctaSubtitle}>
                            Join our 10-year strategic academic network or discuss an institutional MoU for your department. Our executive office coordinates directly with institutional heads nationwide.
                        </p>
                        <div style={styles.ctaContacts}>
                            <a href="https://mail.google.com/mail/?view=cm&fs=1&to=dr.sabitha@arivunest.com" target="_blank" rel="noopener noreferrer" style={styles.ctaContactLink}>
                                <Mail size={16} color="#34d399" />
                                <span>dr.sabitha@arivunest.com</span>
                            </a>
                            <span style={styles.ctaDot}>&bull;</span>
                            <a href="https://mail.google.com/mail/?view=cm&fs=1&to=nandhakumar@arivunest.com" target="_blank" rel="noopener noreferrer" style={styles.ctaContactLink}>
                                <Mail size={16} color="#34d399" />
                                <span>nandhakumar@arivunest.com</span>
                            </a>
                            <span style={styles.ctaDot}>&bull;</span>
                            <a 
                                href="https://chat.whatsapp.com/E79PDHbC0n286T9ChYsgXo" 
                                target="_blank" 
                                rel="noreferrer" 
                                style={styles.ctaContactLink}
                            >
                                <MessageCircle size={16} color="#25D366" />
                                <span>WhatsApp Community</span>
                            </a>
                            <span style={styles.ctaDot}>&bull;</span>
                            <span style={styles.ctaContactText}>
                                📍 Kovilpatti, Virudhunagar Dist., TN
                            </span>
                        </div>
                    </div>
                    <div style={styles.ctaButtons}>
                        <a 
                            href="https://forms.gle/UfkUncKELYenUJE46" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={styles.ctaPrimaryBtn}
                        >
                            <span>Apply for Induction (₹100)</span>
                            <ArrowRight size={16} />
                        </a>
                        <button 
                            onClick={() => navigate('/verify')} 
                            style={styles.ctaSecondaryBtn}
                        >
                            <span>Verify Any Certificate</span>
                            <FileCheck2 size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    page: {
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        paddingBottom: '80px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: '#0f172a',
    },
    container: {
        maxWidth: '1240px',
        margin: '0 auto',
        padding: '0 24px',
    },
    heroSection: {
        background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
        padding: '60px 0 50px 0',
        textAlign: 'center',
        borderBottom: '1px solid #e2e8f0',
        marginBottom: '50px',
    },
    badgePill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        background: '#ecfdf5',
        border: '1px solid #a7f3d0',
        borderRadius: '999px',
        padding: '6px 16px',
        fontSize: '12.5px',
        fontWeight: '700',
        color: '#065f46',
        marginBottom: '20px',
    },
    heroTitle: {
        fontSize: 'clamp(28px, 4.5vw, 44px)',
        fontWeight: '850',
        color: '#0f172a',
        letterSpacing: '-0.5px',
        lineHeight: 1.2,
        margin: '0 auto 16px auto',
        maxWidth: '850px',
    },
    heroSubtitle: {
        fontSize: 'clamp(15px, 2vw, 17px)',
        color: '#475569',
        maxWidth: '780px',
        margin: '0 auto 36px auto',
        lineHeight: 1.6,
    },
    trustGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '20px',
        maxWidth: '880px',
        margin: '0 auto',
        padding: '18px 24px',
        background: '#ffffff',
        borderRadius: '14px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
    },
    trustItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
    },
    trustValue: {
        fontSize: '22px',
        fontWeight: '800',
        color: '#059669',
    },
    trustLabel: {
        fontSize: '11.5px',
        fontWeight: '600',
        color: '#64748b',
        textAlign: 'center',
    },
    offeringsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '28px',
        marginBottom: '60px',
        alignItems: 'stretch',
    },
    card: {
        background: '#ffffff',
        borderRadius: '18px',
        padding: '36px 28px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
    },
    featuredCard: {
        border: '2px solid #059669',
        boxShadow: '0 12px 35px rgba(5, 150, 105, 0.12)',
        background: '#ffffff',
    },
    featuredBadge: {
        position: 'absolute',
        top: '-13px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
        color: '#ffffff',
        padding: '5px 16px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: '800',
        letterSpacing: '0.6px',
        display: 'inline-flex',
        alignItems: 'center',
        boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
        whiteSpace: 'nowrap',
    },
    cardHeader: {
        marginBottom: '20px',
    },
    iconCircleEmerald: {
        width: '52px',
        height: '52px',
        borderRadius: '12px',
        background: '#ecfdf5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
    },
    iconCircleNavy: {
        width: '52px',
        height: '52px',
        borderRadius: '12px',
        background: '#eff6ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
    },
    iconCircleSlate: {
        width: '52px',
        height: '52px',
        borderRadius: '12px',
        background: '#f0fdfa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
    },
    iconCircleAmber: {
        width: '52px',
        height: '52px',
        borderRadius: '12px',
        background: '#fffbeb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
    },
    cardTitle: {
        fontSize: '21px',
        fontWeight: '800',
        color: '#0f172a',
        margin: '0 0 6px 0',
        lineHeight: 1.3,
    },
    targetAudience: {
        fontSize: '12.5px',
        color: '#64748b',
        fontWeight: '500',
        margin: 0,
        lineHeight: 1.4,
    },
    priceContainer: {
        borderTop: '1px solid #f1f5f9',
        borderBottom: '1px solid #f1f5f9',
        padding: '16px 0',
        marginBottom: '22px',
    },
    priceValue: {
        fontSize: '32px',
        fontWeight: '850',
        color: '#059669',
        letterSpacing: '-0.5px',
        lineHeight: 1.1,
    },
    priceSubtitle: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#64748b',
        marginTop: '4px',
    },
    tenureTag: {
        display: 'inline-block',
        marginTop: '8px',
        padding: '4px 10px',
        background: '#ecfdf5',
        color: '#065f46',
        borderRadius: '6px',
        fontSize: '11.5px',
        fontWeight: '700',
    },
    featuresList: {
        listStyle: 'none',
        padding: 0,
        margin: '0 0 28px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flex: 1,
    },
    featureItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        fontSize: '13.5px',
        lineHeight: 1.5,
        color: '#334155',
    },
    checkIcon: {
        flexShrink: 0,
        marginTop: '2px',
    },
    primaryBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        padding: '14px',
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: '#ffffff',
        border: 'none',
        borderRadius: '10px',
        fontWeight: '700',
        fontSize: '14.5px',
        textDecoration: 'none',
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(5, 150, 105, 0.3)',
        transition: 'transform 0.15s, box-shadow 0.15s',
    },
    navyBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        padding: '13px',
        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
        color: '#ffffff',
        border: 'none',
        borderRadius: '10px',
        fontWeight: '700',
        fontSize: '14px',
        textDecoration: 'none',
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(30, 64, 175, 0.25)',
    },
    amberBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        padding: '14px',
        background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)',
        color: '#ffffff',
        border: 'none',
        borderRadius: '10px',
        fontWeight: '700',
        fontSize: '14px',
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(180, 83, 9, 0.25)',
    },
    outlineBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        padding: '13px',
        background: 'transparent',
        border: '2px solid #0f766e',
        color: '#0f766e',
        borderRadius: '10px',
        fontWeight: '700',
        fontSize: '14px',
        cursor: 'pointer',
    },
    btnDisclaimer: {
        fontSize: '11px',
        color: '#94a3b8',
        textAlign: 'center',
        marginTop: '10px',
        marginBottom: 0,
    },
    matrixSection: {
        background: '#ffffff',
        borderTop: '1px solid #e2e8f0',
        borderBottom: '1px solid #e2e8f0',
        padding: '60px 0',
        marginBottom: '60px',
    },
    sectionHeader: {
        textAlign: 'center',
        marginBottom: '40px',
    },
    sectionTitle: {
        fontSize: 'clamp(24px, 3.5vw, 32px)',
        fontWeight: '800',
        color: '#0f172a',
        margin: '0 0 10px 0',
    },
    sectionSubtitle: {
        fontSize: '15px',
        color: '#64748b',
        margin: 0,
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
        background: '#ffffff',
    },
    th: {
        background: '#f8fafc',
        color: '#1e293b',
        fontWeight: '700',
        padding: '16px 20px',
        borderBottom: '2px solid #e2e8f0',
        textAlign: 'center',
    },
    tdTitle: {
        padding: '15px 20px',
        fontWeight: '600',
        color: '#1e293b',
        borderBottom: '1px solid #f1f5f9',
    },
    tdCenter: {
        padding: '15px 20px',
        textAlign: 'center',
        color: '#475569',
        borderBottom: '1px solid #f1f5f9',
    },
    altRow: {
        background: '#f8fafc',
    },
    faqSection: {
        marginBottom: '60px',
    },
    faqList: {
        maxWidth: '850px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    faqItem: {
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '18px 22px',
        cursor: 'pointer',
        transition: 'background 0.15s',
    },
    faqQuestion: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '14px',
    },
    faqQText: {
        fontSize: '15px',
        fontWeight: '700',
        color: '#0f172a',
        lineHeight: 1.4,
    },
    faqAnswer: {
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #f1f5f9',
    },
    faqAText: {
        fontSize: '14px',
        color: '#475569',
        lineHeight: 1.6,
        margin: 0,
    },
    ctaBanner: {
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '20px',
        padding: '44px 36px',
        color: '#ffffff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '28px',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
    },
    ctaContent: {
        flex: '1 1 500px',
    },
    ctaTitle: {
        fontSize: '24px',
        fontWeight: '800',
        margin: '0 0 10px 0',
        color: '#ffffff',
    },
    ctaSubtitle: {
        fontSize: '14.5px',
        color: '#cbd5e1',
        margin: '0 0 18px 0',
        lineHeight: 1.6,
        maxWidth: '620px',
    },
    ctaContacts: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        fontSize: '13px',
    },
    ctaContactLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        color: '#34d399',
        textDecoration: 'none',
        fontWeight: '600',
    },
    ctaDot: {
        color: '#64748b',
    },
    ctaContactText: {
        color: '#94a3b8',
    },
    ctaButtons: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flexShrink: 0,
        minWidth: '220px',
    },
    ctaPrimaryBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '14px 20px',
        background: '#059669',
        color: '#ffffff',
        borderRadius: '10px',
        fontWeight: '700',
        fontSize: '14.5px',
        textDecoration: 'none',
        boxShadow: '0 4px 14px rgba(5, 150, 105, 0.4)',
    },
    ctaSecondaryBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px 20px',
        background: 'transparent',
        border: '1.5px solid rgba(255, 255, 255, 0.2)',
        color: '#ffffff',
        borderRadius: '10px',
        fontWeight: '600',
        fontSize: '14px',
        cursor: 'pointer',
    },
};

export default Pricing;
